/**
 * The outbox capture service (`specs/offline-attendance-v1/implementation-plan.md`,
 * task FE-8; decisions D5, D8; plan amendments C1, C10, C13; Review Focus 2
 * and 5).
 *
 * Turns ONE confirmed clock action into ONE committed, sealed outbox event —
 * or into a refusal that wrote nothing. It is the only place a punch's wire
 * event is frozen; nothing ever rewrites it afterwards (the sync engine sends
 * the sealed payload as is, with the same `client_event_id`, on every try).
 *
 * The order is load-bearing:
 *   1. validate everything that can refuse the capture — the projection
 *      (`canCapture`, the one capture gate), the stored context (grant, policy,
 *      capability, scope), the policy's `allowed_actions`, the clock STAMP and
 *      the grant window, and the Time Out note — so a refusal writes nothing;
 *   2. check the database still is the one this session resolved its scope
 *      from (the installation id) — a database wiped under the session must not
 *      receive a punch keyed by the old installation;
 *   3. mint the `client_event_id`, build the frozen event and SEAL it — all
 *      before any transaction opens (no `await` but Dexie inside one);
 *   4. `commitCapture()` — one transaction that checks the data-key marker and
 *      that the chain neither gained an unsettled head nor any event at all
 *      since the projection was made (`expectedHead` + `expectedTip`, C13),
 *      allocates the device sequence and adds the row.
 * Only a resolved `{ ok: true }` may be reported to the employee as saved.
 *
 * Times come from ONE `ClockMonitor.stamp()` (C10): `occurred_at` is the
 * stamp's corrected (ERPat-aligned) time, `device_occurred_at` its device
 * time, and the `clock` block its calibration — rounded to integers (C1), so
 * `occurred_at − device_occurred_at` is exactly the `clock.offset_ms` sent.
 * The stored context's calibration is never used: after the monitor kept a
 * precise calibration over an imprecise new one, the context holds the
 * imprecise one.
 *
 * Every field the event carries is shaped so ERPat's `normalize_event` accepts
 * it: an invalid field would come back as a FINAL `invalid_event` rejection of
 * a real punch. Hence the timezone and app-version fallbacks, the code-point
 * note count, and the lone-surrogate repair.
 */
import {
  isAttendanceEventType,
  type AttendanceEventType,
  type CapturedConnectivity,
  type OfflinePolicyV1,
} from '@/contracts/offline-attendance-v1';
import { OFFLINE_GRANT_EARLY_TOLERANCE_MS } from '@/contracts/offline-attendance-policy';
import { canCapture } from '@/services/offline/attendance-projection';
import type { ClockStamp } from '@/services/offline/clock-health';
import { OFFLINE_CLOCK_MAX_OFFSET_MS } from '@/services/offline/clock-health';
import {
  StorageError,
  commitCapture,
  getOrCreateInstallationId,
  outboxAad,
  type StorageErrorKind,
} from '@/services/storage/attendance-storage';
import type { DataKeyReady } from '@/services/storage/offline-keys';
import { SealedDataError, sealJson } from '@/services/storage/sealed';
import type {
  AttendanceProjection,
  CaptureBase,
  CaptureInput,
  OfflineContextRecord,
  OfflineScope,
  OutboxRow,
  SealedEventPayload,
  StoredGrant,
} from '@/types/offline-attendance';
import { IS_DEV } from '@/utils/env';
import { newUuidV4 } from '@/utils/uuid';

/**
 * Everything one capture is frozen from. The orchestrator builds it INSIDE the
 * capture lock, right after reloading the chain and the snapshot and
 * re-projecting (C13), and right after `stamp()` (C10).
 */
export interface CaptureContext {
  scope: OfflineScope;
  key: DataKeyReady;
  /** The scope's stored provisioning: grant, policy, capability. Its `calibration` is never read. */
  context: OfflineContextRecord;
  /** The projection the capture is frozen on: its `base`, its `chain` (head and tip). */
  projection: AttendanceProjection;
  /** ONE `ClockMonitor.stamp()` — the only source of the event's times and clock block (C10). */
  stamp: ClockStamp;
  connectivity: CapturedConnectivity;
  /** The app version sent with the event (ERPat: 1–32 bytes). */
  appVersion: string;
  /** The device's IANA time zone (ERPat: `[A-Za-z0-9_+\-/]{1,64}`). */
  timezone: string;
}

/** Why a capture was refused without writing anything. */
export type OutboxRefusalReason =
  | 'not_allowed'
  | 'note_required'
  | 'note_too_long'
  | 'storage'
  | 'state_changed'
  | 'no_grant'
  | 'clock_unsafe';

export type OutboxCaptureResult =
  | { ok: true; row: OutboxRow; occurredAtMs: number }
  | { ok: false; reason: Exclude<OutboxRefusalReason, 'storage'> }
  | {
      ok: false;
      reason: 'storage';
      /** What the repository reported (`unavailable`, `quota`, `aborted`, `corrupt`, …). */
      storageKind: StorageErrorKind;
      /**
       * The database is no longer the one this session's key and scope were
       * resolved from — it was deleted or re-created under the session (the
       * installation id changed, or the data-key marker no longer names this
       * key). Re-resolve the data key, the installation id and the scope
       * TOGETHER before capturing again; reloading the key alone would commit
       * punches under the old installation.
       */
      wiped: boolean;
    };

/** `new Date(ms).toISOString()` — the contract's `YYYY-MM-DDTHH:MM:SS.sssZ`. Throws `RangeError` for a time no date can hold. */
export function toIsoMs(ms: number): string {
  return new Date(ms).toISOString();
}

/** The earliest and latest instants ERPat parses (`parse_iso_utc` refuses years before 1970; four-digit years). */
const MIN_EVENT_MS = 0;
const MAX_EVENT_MS = Date.UTC(9999, 11, 31, 23, 59, 59, 999);
/** ERPat refuses `clock.uncertainty_ms` above one day. */
const MAX_UNCERTAINTY_MS = 86_400_000;

/** ERPat's `timezone` rule; anything else is sent as {@link UNKNOWN_TIMEZONE}. */
const TIMEZONE_RE = /^[A-Za-z0-9_+\-/]{1,64}$/;
/** What a device whose zone cannot be sent reports — ICU's own name for "unknown", and a valid value for ERPat. */
const UNKNOWN_TIMEZONE = 'Etc/Unknown';
/** ERPat's `app_version` limit, in bytes. */
const MAX_APP_VERSION_BYTES = 32;
const UNKNOWN_APP_VERSION = 'unknown';
const POLICY_VERSION_RE = /^[0-9a-f]{12}$/;
const LONE_SURROGATE_RE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

/**
 * Validate, freeze, seal and commit one capture (see the module docblock for
 * the order). Never throws: every failure is a refusal that wrote nothing.
 */
export async function captureEvent(ctx: CaptureContext, input: CaptureInput): Promise<OutboxCaptureResult> {
  try {
    return await freezeAndCommit(ctx, input);
  } catch (err) {
    // A caller bug (a malformed context or input), never a sync outcome. The
    // commit is the last step and reports its own failures, so nothing was
    // written: fail closed — the punch must not read as saved.
    if (IS_DEV) console.warn('[offline-outbox] capture failed unexpectedly', err instanceof Error ? err.name : typeof err);
    return refuseStorage('unavailable', false);
  }
}

async function freezeAndCommit(ctx: CaptureContext, input: CaptureInput): Promise<OutboxCaptureResult> {
  const action: unknown = input.action;
  if (!isAttendanceEventType(action)) return refuse('not_allowed');
  // THE capture gate: false whenever the projection has no base.
  if (!canCapture(ctx.projection, action)) return refuse('not_allowed');
  const base = ctx.projection.base;
  if (base === null) return refuse('not_allowed');

  const provisioned = usableProvisioning(ctx.context, ctx.scope);
  if (provisioned === null) return refuse('no_grant');
  const { grant, policy } = provisioned;
  // ERPat refuses an action outside the grant's `acts` (issued from the
  // policy's allowed actions) as a FINAL grant_invalid.
  if (!policy.allowed_actions.includes(action)) return refuse('not_allowed');

  const clock = clockBlock(ctx.stamp);
  if (clock === null) return refuse('clock_unsafe');
  if (!withinGrant(grant, ctx.stamp.correctedNowMs)) return refuse('no_grant');

  let note: string | undefined;
  if (action === 'time_out') {
    const checked = checkNote(input.note, policy);
    if (typeof checked !== 'string') return refuse(checked.refusal);
    note = checked;
  }

  // A database wiped under the session holds another installation id (or none,
  // and then this mints the one the re-resolved scope will use): a punch keyed
  // by the old one would never reach ERPat as part of a valid chain.
  try {
    const installationId = await getOrCreateInstallationId();
    if (installationId !== ctx.scope.installationId) return refuseStorage('unavailable', true);
  } catch (err) {
    // IndexedDB is missing, closed or full: nothing can be saved.
    return refuseStorage(storageKindOf(err), false);
  }

  const clientEventId = newUuidV4();
  const payload = frozenEvent(action, ctx, base, grant, policy, clock, note);
  let sealed: OutboxRow['sealed'];
  try {
    sealed = await sealJson(ctx.key, outboxAad(ctx.scope.scopeKey, clientEventId), payload);
  } catch (err) {
    // The key cannot encrypt (or a part of the AAD is invalid): nothing was written.
    return refuseStorage(storageKindOf(err), false);
  }

  const row: OutboxRow = {
    clientEventId,
    scopeKey: ctx.scope.scopeKey,
    kind: 'attendance.clock',
    eventType: action,
    deviceSeq: 0, // allocated by commitCapture
    status: 'pending',
    outcome: null,
    resultCode: null,
    createdAt: ctx.stamp.deviceNowMs,
    attempts: 0,
    nextAttemptAt: ctx.stamp.deviceNowMs,
    lastAttemptAt: null,
    leaseGeneration: null,
    dependsOn: base.dependsOn,
    localShiftId: base.localShiftId,
    grantId: grant.grantId,
    settledAt: null,
    sealed,
  };

  try {
    const committed = await commitCapture({
      row,
      expectedHead: base.dependsOn,
      expectedTip: chainTip(ctx.projection),
    });
    return { ok: true, row: committed, occurredAtMs: ctx.stamp.correctedNowMs };
  } catch (err) {
    // Nothing was written: the transaction refused or failed as a whole.
    if (err instanceof StorageError && err.kind === 'state_changed') return refuse('state_changed');
    return refuseStorage(storageKindOf(err), isKeyMismatch(err));
  }
}

/* ── The frozen event ──────────────────────────────────────────────────── */

function frozenEvent(
  action: AttendanceEventType,
  ctx: CaptureContext,
  base: CaptureBase,
  grant: StoredGrant,
  policy: OfflinePolicyV1,
  clock: SealedEventPayload['clock'],
  note: string | undefined,
): SealedEventPayload {
  return {
    event_type: action,
    occurred_at: toIsoMs(ctx.stamp.correctedNowMs),
    device_occurred_at: toIsoMs(ctx.stamp.deviceNowMs),
    timezone: wireTimezone(ctx.timezone),
    clock,
    grant_id: grant.grantId,
    policy_version: policy.version,
    base: {
      attendance_id: base.attendanceId,
      attendance_revision: base.attendanceRevision,
      local_shift_id: base.localShiftId,
      expected_state: base.expectedState,
      expected_break_count: base.expectedBreakCount,
    },
    depends_on_event_id: base.dependsOn,
    ...(note !== undefined ? { note } : {}),
    captured_connectivity: ctx.connectivity,
    app_version: wireAppVersion(ctx.appVersion),
  };
}

/** The newest event of the chain the projection was made from, any status (C13). */
function chainTip(projection: AttendanceProjection): string | null {
  const last = projection.chain[projection.chain.length - 1];
  return last === undefined ? null : last.clientEventId;
}

/* ── Validation ────────────────────────────────────────────────────────── */

/**
 * The grant and policy a capture is made under — `null` when the stored
 * context cannot authorize one: capture is off, nothing is provisioned, or the
 * record is another scope's.
 */
function usableProvisioning(
  context: OfflineContextRecord,
  scope: OfflineScope,
): { grant: StoredGrant; policy: OfflinePolicyV1 } | null {
  const { grant, policy } = context;
  if (!context.capability.offlineAttendance || grant === null || policy === null) return null;
  if (!sameScope(context.scope, scope)) return null;
  if (!POLICY_VERSION_RE.test(policy.version)) return null;
  return { grant, policy };
}

/**
 * Is the corrected time inside the grant's window, as ERPat checks it
 * (`vfr − 300 s ≤ occurred_at ≤ vut`)? Readiness already blocks an expired
 * grant; this keeps a punch ERPat would FINAL-reject from ever being saved.
 */
function withinGrant(grant: StoredGrant, correctedNowMs: number): boolean {
  return correctedNowMs >= grant.validFromMs - OFFLINE_GRANT_EARLY_TOLERANCE_MS && correctedNowMs <= grant.validUntilMs;
}

/**
 * The event's `clock` block from the stamp's calibration (C1: integers) — or
 * `null` when the stamp cannot date a punch: the monitor says the clock is
 * unsafe, a time is not finite or outside what ERPat parses, the corrected
 * time does not follow from the calibration (a stamp assembled from two
 * sources), or a measurement is beyond ERPat's bounds.
 */
function clockBlock(stamp: ClockStamp): SealedEventPayload['clock'] | null {
  if (stamp.verdict.status !== 'safe') return null;
  const { calibration, deviceNowMs, correctedNowMs } = stamp;
  if (!inEventRange(deviceNowMs) || !inEventRange(correctedNowMs)) return null;
  if (!Number.isFinite(calibration.offsetMs) || !Number.isFinite(calibration.uncertaintyMs)) return null;
  const offsetMs = Math.round(calibration.offsetMs) + 0; // `+ 0` turns −0 into 0
  const uncertaintyMs = Math.ceil(calibration.uncertaintyMs) + 0;
  if (correctedNowMs - deviceNowMs !== offsetMs) return null;
  if (Math.abs(offsetMs) > OFFLINE_CLOCK_MAX_OFFSET_MS) return null;
  if (uncertaintyMs < 0 || uncertaintyMs > MAX_UNCERTAINTY_MS) return null;
  const calibratedAtMs = Date.parse(calibration.calibratedAtServerIso);
  if (!inEventRange(calibratedAtMs)) return null;
  return { offset_ms: offsetMs, calibrated_at: calibration.calibratedAtServerIso, uncertainty_ms: uncertaintyMs };
}

function inEventRange(ms: number): boolean {
  return Number.isInteger(ms) && ms >= MIN_EVENT_MS && ms <= MAX_EVENT_MS;
}

/**
 * The Time Out note as it is sealed: trimmed, a lone surrogate replaced (JSON
 * would carry it as an escape ERPat cannot decode, failing the WHOLE upload).
 * Always required — today's UX, whatever `note_required_on_time_out` says — and
 * at most `note_max_length` characters counted as ERPat counts them
 * (`mb_strlen`: code points).
 */
function checkNote(raw: string | undefined, policy: OfflinePolicyV1): string | { refusal: 'note_required' | 'note_too_long' } {
  const note = typeof raw === 'string' ? raw.replace(LONE_SURROGATE_RE, '�').trim() : '';
  if (note.length === 0) return { refusal: 'note_required' };
  if (Array.from(note).length > policy.note_max_length) return { refusal: 'note_too_long' };
  return note;
}

function wireTimezone(timezone: string): string {
  return typeof timezone === 'string' && TIMEZONE_RE.test(timezone) ? timezone : UNKNOWN_TIMEZONE;
}

function wireAppVersion(appVersion: string): string {
  if (typeof appVersion !== 'string') return UNKNOWN_APP_VERSION;
  const value = appVersion.trim();
  const bytes = new TextEncoder().encode(value).length;
  return bytes > 0 && bytes <= MAX_APP_VERSION_BYTES ? value : UNKNOWN_APP_VERSION;
}

function sameScope(a: OfflineScope, b: OfflineScope): boolean {
  return (
    a.apiOrigin === b.apiOrigin &&
    a.companyKey === b.companyKey &&
    a.userId === b.userId &&
    a.installationId === b.installationId &&
    a.scopeKey === b.scopeKey
  );
}

/* ── Refusals ──────────────────────────────────────────────────────────── */

function refuse(reason: Exclude<OutboxRefusalReason, 'storage'>): OutboxCaptureResult {
  return { ok: false, reason };
}

function refuseStorage(storageKind: StorageErrorKind, wiped: boolean): OutboxCaptureResult {
  return { ok: false, reason: 'storage', storageKind, wiped };
}

function storageKindOf(err: unknown): StorageErrorKind {
  return err instanceof StorageError ? err.kind : 'unavailable';
}

/** `commitCapture`'s refusal of a key its database's marker does not name: the database was re-created. */
function isKeyMismatch(err: unknown): boolean {
  return err instanceof StorageError && err.kind === 'corrupt' && err.cause instanceof SealedDataError && err.cause.kind === 'key_mismatch';
}
